import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useUser } from '@clerk/clerk-react'
import { useThemeColors } from '../hooks/useThemeColors'
import {
  getInvestor,
  updateInvestor,
  createContact as addInvestorContact,
  updateContact as updateInvestorContact,
  deleteContact as deleteInvestorContact,
  makeContactPrimary as makeInvestorContactPrimary,
  logInvestorInteraction,
  prepInvestor,
  composeInvestorEmail,
  type InvestorDetail,
  type InvestorContact,
  type InvestorInteraction,
  type InvestorBrief,
  type InvestorStage,
} from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

const STAGES: { value: InvestorStage; label: string }[] = [
  { value: 'researching', label: 'Researching' },
  { value: 'reached_out', label: 'Reached Out' },
  { value: 'meeting_booked', label: 'Meeting Booked' },
  { value: 'pitched', label: 'Pitched' },
  { value: 'diligence', label: 'Diligence' },
  { value: 'passed', label: 'Passed' },
  { value: 'committed', label: 'Committed' },
]

const CHANNELS = ['call', 'email', 'linkedin', 'meeting', 'demo', 'note', 'other'] as const
const OUTCOMES = [
  { value: 'interested', label: 'Interested' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'no_response', label: 'No response' },
  { value: 'converted', label: 'Converted' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
] as const

const FUND_STAGES = ['pre-seed', 'seed', 'series-a', 'series-b', 'growth', 'multi-stage']
const CONTACT_PATHS = ['cold_email', 'warm_intro', 'linkedin', 'conference', 'referral', 'other']

const CHANNEL_ICONS: Record<string, string> = {
  call: '📞',
  email: '✉',
  linkedin: '💼',
  meeting: '🤝',
  demo: '🖥',
  note: '📝',
  other: '💬',
}

const TEMPLATE_OPTIONS = [
  { value: 'investor_cold', label: 'Investor Cold Email' },
  { value: 'warm_intro', label: 'Warm Intro' },
  { value: 'partnership', label: 'Partnership Pitch' },
  { value: 'linkedin_post', label: 'LinkedIn Post' },
  { value: 'x_thread', label: 'X Thread' },
  { value: 'researcher', label: 'Researcher Outreach' },
]

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'direct', label: 'Direct' },
  { value: 'warm', label: 'Warm' },
]


// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days < 1) return 'today'
  if (days === 1) return '1d ago'
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function outcomeBadge(outcome: string | null) {
  if (!outcome) return null
  const map: Record<string, string> = {
    interested: 'bg-green-500/20 text-green-400',
    converted: 'bg-green-500/20 text-green-400',
    follow_up: 'bg-signal/20 text-signal',
    no_response: 'bg-carbon text-steel',
    not_interested: 'bg-red-500/20 text-red-400',
    unsubscribed: 'bg-red-500/20 text-red-400',
  }
  return (
    <span className={`px-2 py-0.5 rounded text-footnote font-medium capitalize ${map[outcome] ?? 'bg-carbon text-steel'}`}>
      {outcome.replace(/_/g, ' ')}
    </span>
  )
}

// ─── Inline Editable Field ────────────────────────────────────────────────────

interface InlineFieldProps {
  label: string
  value: string | null | undefined
  onSave: (val: string | null) => void
  multiline?: boolean
  inputType?: string
  selectOptions?: { value: string; label: string }[]
}

function InlineField({ label, value, onSave, multiline, inputType, selectOptions }: InlineFieldProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement & HTMLSelectElement>(null)

  useEffect(() => {
    setDraft(value ?? '')
  }, [value])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  function handleBlur() {
    setEditing(false)
    const trimmed = draft.trim()
    if (trimmed !== (value ?? '')) {
      onSave(trimmed || null)
    }
  }

  const displayValue = value ?? <span className="text-steel italic">—</span>

  if (editing) {
    if (selectOptions) {
      return (
        <div>
          <label className="block text-footnote text-steel mb-1">{label}</label>
          <select
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={handleBlur}
            className="w-full bg-carbon border border-signal rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none"
          >
            {selectOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )
    }
    if (multiline) {
      return (
        <div>
          <label className="block text-footnote text-steel mb-1">{label}</label>
          <textarea
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={handleBlur}
            rows={4}
            className="w-full bg-carbon border border-signal rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none resize-none"
          />
        </div>
      )
    }
    return (
      <div>
        <label className="block text-footnote text-steel mb-1">{label}</label>
        <input
          ref={inputRef}
          type={inputType ?? 'text'}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={handleBlur}
          className="w-full bg-carbon border border-signal rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none"
        />
      </div>
    )
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className="cursor-pointer group"
    >
      <label className="block text-footnote text-steel mb-0.5 cursor-pointer">{label}</label>
      <p className="text-body-sm text-chalk group-hover:bg-carbon/40 rounded px-1 -ml-1 py-0.5 transition-colors">
        {displayValue}
      </p>
    </div>
  )
}

// ─── Prep Tab ─────────────────────────────────────────────────────────────────

interface PrepTabProps {
  investorId: string
  lastBrief: InvestorBrief | null
  onBriefUpdate: (brief: InvestorBrief) => void
}

function PrepTab({ investorId, lastBrief, onBriefUpdate }: PrepTabProps) {
  const [meetingContext, setMeetingContext] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [brief, setBrief] = useState<InvestorBrief | null>(lastBrief)

  useEffect(() => {
    setBrief(lastBrief)
  }, [lastBrief])

  async function handleGenerate() {
    setLoading(true)
    setError(null)
    try {
      const result = await prepInvestor(investorId, meetingContext || undefined)
      setBrief(result)
      onBriefUpdate(result)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to generate brief')
    } finally {
      setLoading(false)
    }
  }

  function renderBriefSection(key: string, value: unknown) {
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    if (Array.isArray(value)) {
      return (
        <div key={key} className="space-y-1">
          <h4 className="text-body-sm font-semibold text-chalk">{label}</h4>
          <ul className="space-y-1">
            {value.map((item: string, i: number) => (
              <li key={i} className="text-body-sm text-steel flex gap-2">
                <span className="text-signal shrink-0">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )
    }
    return (
      <div key={key} className="space-y-1">
        <h4 className="text-body-sm font-semibold text-chalk">{label}</h4>
        <p className="text-body-sm text-steel">{String(value)}</p>
      </div>
    )
  }

  const BRIEF_KEY_ORDER = [
    'background',
    'company',
    'connection_to_primate',
    'suggested_agenda',
    'questions_to_ask',
    'sensitivities',
    'key_points_about_primate',
  ]

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-body-sm text-steel mb-1">Meeting Context</label>
        <textarea
          value={meetingContext}
          onChange={e => setMeetingContext(e.target.value)}
          rows={2}
          placeholder="General introductory meeting..."
          className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal resize-none"
        />
      </div>

      <button
        onClick={handleGenerate}
        disabled={loading}
        className="w-full bg-signal text-white px-4 py-2 rounded text-body-sm font-medium hover:bg-signal/90 disabled:opacity-50"
      >
        {loading ? '⏳ Researching...' : brief ? 'Regenerate Brief' : 'Generate Brief'}
      </button>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2 text-red-400 text-body-sm">
          {error}
        </div>
      )}

      {brief && (
        <div className="space-y-4 pt-2 border-t border-carbon">
          {BRIEF_KEY_ORDER
            .filter(k => k in brief)
            .map(k => renderBriefSection(k, brief[k]))
          }
          {Object.keys(brief)
            .filter(k => !BRIEF_KEY_ORDER.includes(k))
            .map(k => renderBriefSection(k, brief[k]))
          }
        </div>
      )}
    </div>
  )
}

// ─── Contacts Section ────────────────────────────────────────────────────────

interface ContactsSectionProps {
  investorId: string
  contacts: InvestorContact[]
  onContactsChanged: (contacts: InvestorContact[]) => void
}

function ContactsSection({ investorId, contacts, onContactsChanged }: ContactsSectionProps) {
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newLinkedin, setNewLinkedin] = useState('')
  const [newReceives, setNewReceives] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  async function handleAdd() {
    if (!newName.trim()) return
    setSaving(true)
    try {
      const c = await addInvestorContact(investorId, {
        name: newName.trim(),
        email: newEmail.trim() || null,
        linkedin: newLinkedin.trim() || null,
        receives_updates: newReceives,
      })
      onContactsChanged([...contacts, c])
      setAdding(false)
      setNewName(''); setNewEmail(''); setNewLinkedin(''); setNewReceives(false)
    } catch (e) {
      console.error('Failed to add contact', e)
    } finally {
      setSaving(false)
    }
  }

  async function handlePatch(c: InvestorContact, field: keyof InvestorContact, value: unknown) {
    try {
      const updated = await updateInvestorContact(investorId, c.id, { [field]: value } as Partial<InvestorContact>)
      onContactsChanged(contacts.map(x => x.id === c.id ? updated : x))
    } catch (e) {
      console.error('Failed to update contact', e)
    }
  }

  async function handleDelete(c: InvestorContact) {
    if (contacts.length <= 1) return
    if (!window.confirm(`Remove ${c.name} from this firm?`)) return
    try {
      await deleteInvestorContact(investorId, c.id)
      onContactsChanged(contacts.filter(x => x.id !== c.id))
    } catch (e) {
      console.error('Failed to delete contact', e)
    }
  }

  async function handleMakePrimary(c: InvestorContact) {
    if (c.is_primary) return
    try {
      await makeInvestorContactPrimary(investorId, c.id)
      onContactsChanged(contacts.map(x => ({ ...x, is_primary: x.id === c.id })))
    } catch (e) {
      console.error('Failed to make primary', e)
    }
  }

  return (
    <div className="space-y-3">
      {contacts.map((c) => (
        <div key={c.id} className="bg-obsidian border border-carbon rounded p-3 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              {c.is_primary ? (
                <span className="px-1 py-0.5 text-footnote bg-signal/15 text-signal rounded font-medium leading-none">Primary</span>
              ) : (
                <button
                  onClick={() => void handleMakePrimary(c)}
                  className="px-1 py-0.5 text-footnote bg-void text-steel hover:text-chalk border border-carbon rounded font-medium leading-none"
                >
                  Make Primary
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEditingId(editingId === c.id ? null : c.id)}
                className="text-footnote text-steel hover:text-chalk"
              >
                {editingId === c.id ? 'Done' : 'Edit'}
              </button>
              {contacts.length > 1 && (
                <button
                  onClick={() => void handleDelete(c)}
                  className="text-footnote text-red-400 hover:text-red-300"
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          {editingId === c.id ? (
            <div className="space-y-2">
              <input
                defaultValue={c.name}
                onBlur={e => { if (e.target.value.trim() !== c.name) handlePatch(c, 'name', e.target.value.trim()) }}
                placeholder="Name"
                className="w-full bg-carbon border border-signal rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none"
              />
              <input
                defaultValue={c.email ?? ''}
                onBlur={e => handlePatch(c, 'email', e.target.value.trim() || null)}
                placeholder="Email"
                type="email"
                className="w-full bg-carbon border border-signal rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none"
              />
              <input
                defaultValue={c.linkedin ?? ''}
                onBlur={e => handlePatch(c, 'linkedin', e.target.value.trim() || null)}
                placeholder="LinkedIn URL"
                className="w-full bg-carbon border border-signal rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none"
              />
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={c.receives_updates}
                  onChange={e => handlePatch(c, 'receives_updates', e.target.checked)}
                />
                <span className="text-footnote text-steel">Receives investor updates</span>
              </label>
            </div>
          ) : (
            <div className="space-y-0.5">
              <p className="text-body-sm text-chalk font-medium">{c.name}</p>
              {c.email && <p className="text-footnote text-steel">{c.email}</p>}
              {c.linkedin && <p className="text-footnote text-steel">{c.linkedin}</p>}
              {c.receives_updates && <p className="text-footnote text-signal">\u2709 receives updates</p>}
            </div>
          )}
        </div>
      ))}

      {adding ? (
        <div className="bg-obsidian border border-signal rounded p-3 space-y-2">
          <p className="text-footnote font-semibold text-steel uppercase tracking-wide">New Contact</p>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Name *"
            className="w-full bg-carbon border border-carbon rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none focus:border-signal"
          />
          <input
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="Email"
            type="email"
            className="w-full bg-carbon border border-carbon rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none focus:border-signal"
          />
          <input
            value={newLinkedin}
            onChange={e => setNewLinkedin(e.target.value)}
            placeholder="LinkedIn URL"
            className="w-full bg-carbon border border-carbon rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none focus:border-signal"
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={newReceives} onChange={e => setNewReceives(e.target.checked)} />
            <span className="text-footnote text-steel">Receives investor updates</span>
          </label>
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={saving || !newName.trim()}
              className="flex-1 bg-signal text-white px-3 py-1.5 rounded text-body-sm font-medium hover:bg-signal/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Add Contact'}
            </button>
            <button
              onClick={() => { setAdding(false); setNewName(''); setNewEmail(''); setNewLinkedin(''); setNewReceives(false) }}
              className="px-3 py-1.5 rounded text-body-sm text-steel hover:text-chalk"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="text-body-sm text-signal hover:text-signal/80 flex items-center gap-1"
        >
          + Add Contact
        </button>
      )}
    </div>
  )
}

// ─── Compose Tab ──────────────────────────────────────────────────────────────

interface ComposeTabProps {
  investorId: string
}

function ComposeTab({ investorId }: ComposeTabProps) {
  const navigate = useNavigate()
  const [templateType, setTemplateType] = useState('investor_cold')
  const [tone, setTone] = useState('professional')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  async function handleDraft() {
    setLoading(true)
    setError(null)
    setSuccess(false)
    try {
      await composeInvestorEmail(investorId, templateType, tone)
      setSuccess(true)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to compose draft')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-body-sm text-steel mb-1">Template</label>
        <select
          value={templateType}
          onChange={e => setTemplateType(e.target.value)}
          className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
        >
          {TEMPLATE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      <div>
        <label className="block text-body-sm text-steel mb-1">Tone</label>
        <select
          value={tone}
          onChange={e => setTone(e.target.value)}
          className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
        >
          {TONE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      <button
        onClick={handleDraft}
        disabled={loading}
        className="w-full bg-aurora text-white px-4 py-2 rounded text-body-sm font-medium hover:bg-aurora/90 disabled:opacity-50"
      >
        {loading ? '⏳ Drafting...' : 'Draft Email'}
      </button>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2 text-red-400 text-body-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/30 rounded px-3 py-2 text-body-sm">
          <p className="text-green-400">
            ✓ Draft added to email queue →{' '}
            <button
              onClick={() => navigate('/dashboard/admin/emails')}
              className="text-signal hover:underline"
            >
              View Email Queue
            </button>
          </p>
        </div>
      )}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminInvestorDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useUser()
  const c = useThemeColors()
  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? ''
  const isAdmin = ADMIN_EMAILS.includes(email)

  const [investor, setInvestor] = useState<InvestorDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'prep' | 'compose'>('prep')

  // Interaction log state
  const [logChannel, setLogChannel] = useState<typeof CHANNELS[number]>('email')
  const [logDirection, setLogDirection] = useState<'inbound' | 'outbound'>('outbound')
  const [logNotes, setLogNotes] = useState('')
  const [logOutcome, setLogOutcome] = useState('')
  const [logSaving, setLogSaving] = useState(false)
  const [logError, setLogError] = useState<string | null>(null)

  useEffect(() => {
    if (!id || !isAdmin) return
    setLoading(true)
    getInvestor(id)
      .then(setInvestor)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [id, isAdmin])

  async function handleFieldSave(field: string, value: unknown) {
    if (!investor) return
    try {
      const updated = await updateInvestor(investor.id, { [field]: value } as Partial<InvestorDetail>)
      setInvestor(prev => prev ? { ...prev, ...updated, interactions: prev.interactions } : null)
    } catch (e) {
      console.error('Failed to update investor field:', e)
    }
  }

  async function handleLogInteraction() {
    if (!investor) return
    setLogSaving(true)
    setLogError(null)
    try {
      const interaction = await logInvestorInteraction(investor.id, {
        direction: logDirection,
        channel: logChannel,
        notes: logNotes || undefined,
        outcome: logOutcome || undefined,
      })
      setInvestor(prev =>
        prev ? { ...prev, interactions: [interaction, ...prev.interactions] } : null,
      )
      setLogNotes('')
      setLogOutcome('')
    } catch (e: unknown) {
      setLogError(e instanceof Error ? e.message : 'Failed to log interaction')
    } finally {
      setLogSaving(false)
    }
  }

  if (!isAdmin) {
    return <div className="p-8 text-center"><p className="text-steel">Admin access required.</p></div>
  }

  if (loading) {
    return <div className="p-8 text-center"><p className="text-steel">Loading...</p></div>
  }

  if (!investor) {
    return (
      <div className="p-8 text-center">
        <p className="text-steel">Investor not found.</p>
        <button onClick={() => navigate('/dashboard/investors')} className="mt-4 text-signal hover:underline text-body-sm">
          ← Back to Investors
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-void">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-carbon shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="text-steel hover:text-chalk text-body-sm"
        >
          ← Back
        </button>
        <div className="flex-1">
          <h1 className="text-heading-md text-chalk font-semibold">
            {investor.firm}
          </h1>
          {investor.contacts?.[0] && (
            <p className="text-body-sm" style={{ color: c.textMuted }}>
              {investor.contacts[0].name}
              {(investor.contacts.length > 1) && ` +${investor.contacts.length - 1} more`}
            </p>
          )}
        </div>
      </div>

      {/* 2-panel layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel — 60% */}
        <div className="flex-[3] overflow-y-auto border-r border-carbon">
          <div className="p-6 space-y-6">
            {/* Contacts section */}
            <div>
              <h3 className="text-body-sm font-semibold text-chalk mb-3">Contacts</h3>
              <ContactsSection
                investorId={investor.id}
                contacts={investor.contacts ?? []}
                onContactsChanged={contacts =>
                  setInvestor(prev => prev ? { ...prev, contacts } : null)
                }
              />
            </div>

            {/* Firm Details */}
            <div>
              <h3 className="text-body-sm font-semibold text-chalk mb-3">Firm Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <InlineField label="Firm" value={investor.firm} onSave={v => handleFieldSave('firm', v)} />
                <InlineField
                  label="Stage"
                  value={investor.stage}
                  onSave={v => handleFieldSave('stage', v)}
                  selectOptions={STAGES.map(s => ({ value: s.value, label: s.label }))}
                />
                <InlineField
                  label="Fund Stage"
                  value={investor.fund_stage}
                  onSave={v => handleFieldSave('fund_stage', v)}
                  selectOptions={[{ value: '', label: '—' }, ...FUND_STAGES.map(s => ({ value: s, label: s }))]}
                />
                <InlineField label="Check Size" value={investor.check_size} onSave={v => handleFieldSave('check_size', v)} />
                <InlineField
                  label="Contact Path"
                  value={investor.contact_path}
                  onSave={v => handleFieldSave('contact_path', v)}
                  selectOptions={[{ value: '', label: '—' }, ...CONTACT_PATHS.map(p => ({ value: p, label: p.replace(/_/g, ' ') }))]}
                />
                <InlineField label="Next Action" value={investor.next_action} onSave={v => handleFieldSave('next_action', v)} />
                <InlineField label="Next Action Due" value={investor.next_action_due} onSave={v => handleFieldSave('next_action_due', v)} inputType="date" />
                <InlineField label="Last Contacted" value={investor.last_contacted} onSave={v => handleFieldSave('last_contacted', v)} inputType="date" />
                {/* Investment fields */}
                <div className="flex items-center gap-3 col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={investor.is_current_investor ?? false}
                      onChange={() => handleFieldSave('is_current_investor', !investor.is_current_investor)}
                    />
                    <span className="text-footnote text-steel">Current Investor</span>
                  </label>
                </div>
                {(investor.is_current_investor || investor.amount_invested != null) && (
                  <InlineField
                    label="Investment ($)"
                    value={investor.amount_invested != null ? String(investor.amount_invested) : ''}
                    onSave={v => handleFieldSave('amount_invested', v ? parseInt(v, 10) : null)}
                    inputType="number"
                  />
                )}
              </div>
              <div className="mt-4">
                <InlineField label="Notes" value={investor.notes} onSave={v => handleFieldSave('notes', v)} multiline />
              </div>
            </div>

            {/* Interaction log */}
            <div>
              <h3 className="text-body-sm font-semibold text-chalk mb-3">Log Interaction</h3>
              <div className="bg-obsidian border border-carbon rounded p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-footnote text-steel mb-1">Channel</label>
                    <select
                      value={logChannel}
                      onChange={e => setLogChannel(e.target.value as typeof CHANNELS[number])}
                      className="w-full bg-carbon border border-carbon rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none focus:border-signal"
                    >
                      {CHANNELS.map(c => <option key={c} value={c}>{CHANNEL_ICONS[c]} {c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-footnote text-steel mb-1">Direction</label>
                    <select
                      value={logDirection}
                      onChange={e => setLogDirection(e.target.value as 'inbound' | 'outbound')}
                      className="w-full bg-carbon border border-carbon rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none focus:border-signal"
                    >
                      <option value="outbound">Outbound</option>
                      <option value="inbound">Inbound</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-footnote text-steel mb-1">Notes</label>
                  <input
                    value={logNotes}
                    onChange={e => setLogNotes(e.target.value)}
                    placeholder="What happened?"
                    className="w-full bg-carbon border border-carbon rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none focus:border-signal"
                  />
                </div>
                <div>
                  <label className="block text-footnote text-steel mb-1">Outcome</label>
                  <select
                    value={logOutcome}
                    onChange={e => setLogOutcome(e.target.value)}
                    className="w-full bg-carbon border border-carbon rounded px-2 py-1.5 text-chalk text-body-sm focus:outline-none focus:border-signal"
                  >
                    <option value="">— no outcome —</option>
                    {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {logError && (
                  <p className="text-red-400 text-footnote">{logError}</p>
                )}
                <button
                  onClick={handleLogInteraction}
                  disabled={logSaving}
                  className="w-full bg-signal text-white px-3 py-2 rounded text-body-sm font-medium hover:bg-signal/90 disabled:opacity-50"
                >
                  {logSaving ? 'Saving...' : 'Log Interaction'}
                </button>
              </div>
            </div>

            {/* Interaction timeline */}
            {investor.interactions.length > 0 && (
              <div>
                <h3 className="text-body-sm font-semibold text-chalk mb-3">
                  Interactions ({investor.interactions.length})
                </h3>
                <div className="space-y-2">
                  {investor.interactions.map((interaction: InvestorInteraction) => (
                    <div
                      key={interaction.id}
                      className="flex gap-3 items-start py-2 border-b border-carbon/50"
                    >
                      <span className="text-body-lg shrink-0 mt-0.5">{CHANNEL_ICONS[interaction.channel] ?? '💬'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-body-sm text-chalk capitalize">{interaction.channel}</span>
                          <span className="text-footnote text-steel capitalize">{interaction.direction}</span>
                          {outcomeBadge(interaction.outcome)}
                          <span className="text-footnote text-steel ml-auto">{timeAgo(interaction.occurred_at)}</span>
                        </div>
                        {interaction.notes && (
                          <p className="text-footnote text-steel mt-1">{interaction.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right panel — 40% */}
        <div className="flex-[2] flex flex-col overflow-hidden">
          {/* Tab bar */}
          <div className="flex border-b border-carbon shrink-0">
            {(['prep', 'compose'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 px-4 py-3 text-body-sm font-medium capitalize transition-colors ${
                  activeTab === tab
                    ? 'text-chalk border-b-2 border-signal'
                    : 'text-steel hover:text-chalk'
                }`}
              >
                {tab === 'prep' ? '🔍 Prep' : '✍ Compose'}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'prep' ? (
              <PrepTab
                investorId={investor.id}
                lastBrief={investor.last_brief}
                onBriefUpdate={brief =>
                  setInvestor(prev => prev ? { ...prev, last_brief: brief } : null)
                }
              />
            ) : (
              <ComposeTab
                investorId={investor.id}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
